import { useMemo, useState } from 'react';
import { useLoaderData } from 'react-router-dom';
import ClientLogo from '../components/logoClient';
import ToggleGroup from '../components/toggleGroup';
import RadioSelector from '../components/outcome-tracker/radioButtonGroup';
import CampaignScope from '../components/outcome-tracker/campaignScope';
import HouseholdReferencesScope from '../components/outcome-tracker/householdReferencesScope';
import GetAllCampaigns from '../services/adapters/allCampaigns';
import { DisplayCampaignInformation } from '../types/campaigns.type';
import GetAllSnapshots from '../services/adapters/allSnapshots';
import { Snapshot } from '../types/core.type';
import Chip from '../components/chip';
import { ChipProps, CohortScopeProps } from '../types/buttons.type';
import { HeadlineCardProps, OutcomeTrackerHeadlines } from '../types/global';
import useRequestState from '../hooks/useRequestState';
import HeadlineCard from '../components/home/headlineCard';
import Button from '../components/buttons/baseButton';
import { FrontendFilterValue } from '../types/filters.type';
import { CohortType, FilterModalType, OutcomeTrackerType } from '../types/enums';
import { MetricRow, OutcomeDetailedMetricsTab } from '../types/table.type';
import ExpandTable from '../components/expandTable';
import TabButton from '../components/benefits/tabButton';
import customIcons from '../utils/icons';
import Tooltip from '../components/toolTip';
import PositiveOutcome from '../components/outcome-tracker/positiveOutcome';
import CohortScope from '../components/outcome-tracker/cohortSelector';
import PostAllCohorts from '../services/adapters/allAnalysisCohorts';
import runAnalysis from '../components/outcome-tracker/runAnalysis';
import { replaceMatchingTab, replaceMatchingRow } from '../components/outcome-tracker/replaceDetailedMetricsTableData';
import PostDeleteCohort from '../services/adapters/deleteCohort';
import { useFilterModal } from '../components/modalProvider';
import { CategoricalData } from '../api-models';
import PostBenefitTakeupGraph from '../services/adapters/outcomeTrackerBenefitTakeupGraph';
import { OutcomeTrackerAdaptRequestProps } from '../services/adapters/outcomeTrackerAdaptRequest';
import GraphContainer from '../components/outcome-tracker/graphContainer';
import useAllFilterStore from '../shared/hooks/allFilterStore';
import { filterCount } from '../utils/filters';

function buildMetricTableRow(
  row: MetricRow,
  lineGraphCache: Map<string, CategoricalData[]>,
  detailedMetricsSlowRow: OutcomeDetailedMetricsTab | null | undefined,
  dedicatedQueryRowNames: Set<string>,
  isSlowTab: boolean,
) {
  const graphData = row.id ? lineGraphCache.get(row.id) : undefined;
  return {
    id: row.id!,
    loading: !detailedMetricsSlowRow && dedicatedQueryRowNames.has(row.cells[0]?.value),
    data: Object.fromEntries(row.cells.map((cell, colIndex) => [`col_${colIndex}`, cell.value])),
    trends: Object.fromEntries(row.cells.map((cell, colIndex) => [`col_${colIndex}`, cell.trend])),
    detail: isSlowTab && (
      <div className="h-[250px] p-5 pt-10 pb-5">
        <GraphContainer graphData={graphData} />
      </div>
    ),
  };
}

export function loader() {
  const snapshotAdapter = new GetAllSnapshots();
  const snapshots = snapshotAdapter.adaptResponse();
  const campaignAdapter = new GetAllCampaigns();
  const campaigns = campaignAdapter.adaptResponse();
  return Promise.all([snapshots, campaigns]);
}

export default function OutcomeTracker() {
  const [snapshotsResponse, campaignApiResponse] = useLoaderData<[Snapshot[], DisplayCampaignInformation[]]>();
  const [selectedCohort, setSelectedCohort] = useState<CohortType>(CohortType.Campaign);
  const [selectSavedCohort, setSelectedSavedCohort] = useState(''); // for load saved option
  const [savedCohorts, setSavedCohorts] = useState<CohortScopeProps[]>([]);
  const [campaign, setCampaign] = useState('');
  const [snapshots, setSnapshots] = useState({ start: '', end: '' });
  const [householdReferences, setHouseholdReferences] = useState<FrontendFilterValue[]>([]);
  const [selectedTabName, setSelectedTabName] = useState<string>('');
  const [currentOutcomeRequest, setCurrentOutcomeRequest] = useState<OutcomeTrackerAdaptRequestProps | null>(null);
  const [lineGraphCache, setLineGraphCache] = useState<Map<string, CategoricalData[]>>(new Map());
  const [expandedTableRowId, setExpandedTableRowId] = useState<string | undefined>();
  const { openFilterModal, closeModal } = useFilterModal();
  const { filterTypeLookup } = useAllFilterStore();
  const radioOptions = [
    { value: CohortType.Campaign, label: 'Campaign' },
    { value: CohortType.HouseholdReferences, label: 'Paste Household References' },
    { value: CohortType.Cohort, label: 'Load Saved' },
  ];

  const checkValidRun = () => {
    if (selectedCohort === CohortType.Campaign) {
      return campaign !== '' && snapshots.start !== '' && snapshots.end !== '';
    }
    if (selectedCohort === CohortType.HouseholdReferences) {
      return householdReferences.length > 0 && snapshots.start !== '' && snapshots.end !== '';
    }
    if (selectedCohort === CohortType.Cohort) {
      return selectSavedCohort !== '' && snapshots.start !== '' && snapshots.end !== '';
    }
    return false;
  };

  const campaignsForDropdown = campaignApiResponse.map((apiCampaign) => ({
    id: apiCampaign.campaignCommunicationId?.toString() ?? '',
    name: apiCampaign.campaignName,
    snapshot: apiCampaign.snapshot,
    rawSnapshot: apiCampaign.rawSnapshot,
  }));

  const snapshotOptions = snapshotsResponse.map((snapshot) => ({
    value: snapshot.rawSnapshot,
    label: snapshot.formattedSnapshot,
  }));

  const {
    data: headlineCards,
    handleRequest: handleHeadlineCardsRequest,
    FallbackWrapper: HeadlineCardsFallbackWrapper,
  } = useRequestState<OutcomeTrackerHeadlines>();

  const {
    data: detailedMetricsFastTabs,
    handleRequest: handleDetailedMetricsFastTabsRequest,
    FallbackWrapper: DetailedMetricsFastTabsFallbackWrapper,
  } = useRequestState<OutcomeDetailedMetricsTab[]>();

  const {
    data: detailedMetricsSlowTabs,
    handleRequest: handleDetailedMetricsSlowTabsRequest,
    FallbackWrapper: DetailedMetricsSlowTabsFallbackWrapper,
  } = useRequestState<OutcomeDetailedMetricsTab[]>();

  const {
    data: detailedMetricsSlowRow,
    handleRequest: handleDetailedMetricsSlowRowRequest,
  } = useRequestState<OutcomeDetailedMetricsTab>();

  const {
    loading: isExpandedRowLoading,
    handleRequest: handleExpandedRowRequest,
  } = useRequestState<CategoricalData[]>();

  let detailedMetricsTabs = detailedMetricsFastTabs ?? [];

  (detailedMetricsSlowTabs ?? []).forEach((tab) => {
    detailedMetricsTabs = replaceMatchingTab(detailedMetricsTabs, tab);
  });
  detailedMetricsTabs = replaceMatchingRow(detailedMetricsTabs, detailedMetricsSlowRow);

  const activeTabName = useMemo(() => {
    if (detailedMetricsTabs.some((tab) => tab.tabName === selectedTabName)) {
      return selectedTabName;
    }
    return detailedMetricsTabs[0]?.tabName ?? '';
  }, [detailedMetricsTabs, selectedTabName]);

  const selectedTab = detailedMetricsTabs.find((tab) => tab.tabName === activeTabName);
  const isSlowTab = detailedMetricsSlowTabs?.some((tab) => tab.tabName === activeTabName) ?? false;

  const handleExpandedRowGraph = async (rowId: string) => {
    if (!currentOutcomeRequest || lineGraphCache.has(rowId) || isExpandedRowLoading) return;
    const graphData = await handleExpandedRowRequest(
      new PostBenefitTakeupGraph().adaptResponse(currentOutcomeRequest, rowId),
    );
    if (graphData) setLineGraphCache((existingGraphData) => new Map([...existingGraphData, [rowId, graphData]]));
  };

  const summaryStats = useMemo<ChipProps[]>(() => {
    if (!headlineCards?.summary) {
      return [];
    }
    const summaryChipProps: ChipProps[] = [{
      text: headlineCards.summary.analysisName,
      colour: 'green',
      closeButton: false,
    }, {
      text: `${headlineCards.summary.start} → ${headlineCards.summary.end}`,
      colour: 'default',
      closeButton: false,
    }, {
      text: headlineCards.summary.numberOfHouseholdsStillInData,
      colour: 'default',
      closeButton: false,
    }];

    if (headlineCards.summary.filters) {
      summaryChipProps.splice(
        1,
        0,
        {
          text: `Filters (${filterCount(headlineCards.summary.filters, filterTypeLookup)})`,
          colour: 'default',
          closeButton: false,
          onClick: () => {
            openFilterModal({
              mode: FilterModalType.Summary,

              filters: headlineCards.summary.filters,
              isOpen: true,
              onClose: closeModal,
            });
          },

        },
      );
    }
    if (householdReferences.length > 0) {
      summaryChipProps.splice(-1, 0, {
        text: `${householdReferences.length} references entered`,
        colour: 'default',
        closeButton: false,
      });
    }
    return summaryChipProps;
  }, [headlineCards, openFilterModal, closeModal, householdReferences]);

  const getSavedCohorts = async () => {
    const cohortAdapter = new PostAllCohorts();
    const cohortsResponse = await cohortAdapter.adaptResponse();
    setSavedCohorts(cohortsResponse);
  };

  const resetAnalysisInputs = () => {
    setCampaign('');
    setHouseholdReferences([]);
  };

  const handleCohortTypeChange = (cohortType: string) => {
    setSelectedCohort(cohortType as CohortType);
    setSelectedSavedCohort('');
    setSnapshots({ start: '', end: '' });
    resetAnalysisInputs();

    if ((cohortType as CohortType) === CohortType.Cohort) {
      void getSavedCohorts();
    }
  };

  const handleSavedCohortChange = (cohortId: string) => {
    setSelectedSavedCohort(cohortId);
    const selectedCohortData = savedCohorts.find((cohort) => cohort.id === cohortId);
    if (selectedCohortData) {
      setSnapshots({ start: selectedCohortData.start, end: selectedCohortData.end });
    } else {
      setSnapshots({ start: '', end: '' });
    }
    resetAnalysisInputs();
  };

  const deleteCohort = async (cohortId: string) => {
    const adapter = new PostDeleteCohort();
    await adapter.adaptResponse(cohortId);
    await getSavedCohorts();
    if (cohortId === selectSavedCohort) {
      setSelectedSavedCohort('');
      setSnapshots({ start: '', end: '' });
    }
  };

  const handleExpandedRowChange = (rowId: string | undefined) => {
    setExpandedTableRowId(rowId);
    if (rowId !== undefined && isSlowTab) {
      void handleExpandedRowGraph(rowId);
    }
  };

  const handleRunAnalysis = () => {
    setCurrentOutcomeRequest({
      selectedCohort,
      campaign,
      householdReferences,
      snapshots,
      savedCohortId: selectSavedCohort,
    });
    setLineGraphCache(new Map());
    setExpandedTableRowId(undefined);
    void runAnalysis(
      selectedCohort,
      campaign,
      householdReferences,
      snapshots,
      handleHeadlineCardsRequest,
      handleDetailedMetricsFastTabsRequest,
      handleDetailedMetricsSlowTabsRequest,
      handleDetailedMetricsSlowRowRequest,
      selectSavedCohort,
    );
  };

  return (
    <div className="flex flex-col w-full h-fit min-h-full py-5 gap-5 bg-gray-50">
      <header className="flex flex-row justify-between items-center mx-8">
        <div id="dashboard-header-text" className="flex flex-col">
          <h1 className="self-stretch justify-start">Outcome Tracker</h1>
          <span className="text-sm text-secondary">
            See the financial outcomes of benefit campaigns and track which residents have been helped to maximise their income
          </span>
        </div>
        <ClientLogo className="w-auto" />
      </header>

      <ToggleGroup
        ariaLabel="Outcome tracker type"
        selected={OutcomeTrackerType.Standard}
        className="w-fit mx-8"
        options={[
          {
            value: OutcomeTrackerType.Standard,
            label: OutcomeTrackerType.Standard
          },
          {
            value: OutcomeTrackerType.ControlGroupAnalysis,
            label: OutcomeTrackerType.ControlGroupAnalysis,
            disabled: true,
            disabledTitle: 'Coming soon',
          },
        ]}
      />

      <div className="flex flex-col gap-5 p-6 mx-8 rounded-lg border border-pie-chart-slice2 bg-white">
        <div id="cohort-by-container" className="flex flex-col sm:flex-row items-start sm:items-center gap-4 border-b border-pie-chart-slice2 pb-8">
          <span className="font-semibold text-base text-secondary mr-2">Cohort by</span>
          <RadioSelector
            options={radioOptions}
            selected={selectedCohort}
            onChange={handleCohortTypeChange}
            name={CohortType.RadioName}
          />
        </div>
        <div id="outcome-analysis-scope-container" className="flex gap-6 w-full">
          <div className="w-full">
            {selectedCohort === CohortType.Campaign && (
            <CampaignScope
              selectedCampaign={campaign}
              onCampaignChange={setCampaign}
              selectedSnapshots={snapshots}
              onSnapshotsChange={setSnapshots}
              campaigns={campaignsForDropdown}
              snapshots={snapshotOptions}
            />

            )}

            {
              selectedCohort === CohortType.HouseholdReferences && (
              <HouseholdReferencesScope
                householdReferences={householdReferences}
                onHouseholdReferencesChange={setHouseholdReferences}
                selectedSnapshots={snapshots}
                onSnapshotsChange={setSnapshots}
                snapshots={snapshotOptions}
              />
              )
            }

            {selectedCohort === CohortType.Cohort && (
              <CohortScope
                cohorts={savedCohorts}
                selectedCohort={selectSavedCohort}
                setSelectedCohort={handleSavedCohortChange}
                deleteCohort={deleteCohort}
              />
            )}
          </div>
        </div>
      </div>
      {summaryStats.length > 0
      && (
      <div className="flex items-center px-8 gap-3 w-full">
        <div>Showing data for: </div>
        {summaryStats.map((chip) => (
          <Chip
            key={chip.text}
            text={chip.text}
            closeButton={chip.closeButton}
            colour={chip.colour}
            tooltip={chip.tooltip}
            small={chip.small}
            onClick={chip.onClick}
          />
        ))}
      </div>
      )}
      <Button
        id="run-outcome-tracking-analysis-button"
        label="Run Analysis"
        onClick={handleRunAnalysis}
        className={`${checkValidRun() ? 'button-base bg-primary-vibrant text-white hover:opacity-90' : 'button-default cursor-not-allowed'} mx-8`}
        isDisabled={!checkValidRun()}
      />
      <div>
        <HeadlineCardsFallbackWrapper loadingMessage="...loading">
          {headlineCards && (headlineCards.cards.length > 0 || headlineCards.positiveOutcome) && (
          <div className="mx-8 my-4">
            {headlineCards.cards.length > 0 && (
            <div className="flex gap-2 justify-start items-center w-full mb-2">
              <h2>Headlines</h2>
              <Tooltip text="All analysis is completed on households that were in the beginning snapshot." label="outcome tracker headlines" />
            </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              {headlineCards.cards.map((card: HeadlineCardProps) => (
                <HeadlineCard
                  key={card.text}
                  text={card.text}
                  tooltip={card.tooltip}
                  displayNumber={card.displayNumber}
                  variance={card.variance}
                  trendUpward={card.trendUpward}
                  icon={card.icon}
                  footer={card.footer}
                />
              ))}
              {headlineCards.positiveOutcome && (
              <div className="col-start-2 col-end-4 row-start-2 row-end-4 h-full">
                <PositiveOutcome
                  icon={headlineCards.positiveOutcome.icon}
                  title={headlineCards.positiveOutcome.title}
                  tooltip={headlineCards.positiveOutcome.tooltip}
                  totalHouseholds={headlineCards.positiveOutcome.displayValue}
                  valueSubtitle={headlineCards.positiveOutcome.valueSubtitle}
                  unit={headlineCards.positiveOutcome.unit}
                  positivePercentage={headlineCards.positiveOutcome.pieChartData.greenValue}
                  positivePercentageLabel={headlineCards.positiveOutcome.pieChartData.label}
                  categories={headlineCards.positiveOutcome.barChartData}
                />
              </div>
              )}
            </div>
          </div>
          )}
        </HeadlineCardsFallbackWrapper>
        <div>
          <DetailedMetricsFastTabsFallbackWrapper loadingMessage="...loading">

            {detailedMetricsTabs.length > 0 && (

            <div className="flex flex-col gap-5 mx-8 rounded-lg border border-pie-chart-slice2 bg-white mb-6">
              <div className="flex gap-2 border-b border-pie-chart-slice2 p-4 items-center">
                <span className="text-primary">{customIcons.barChart}</span>
                <span className="font-semibold text-base text-secondary">Detailed Metrics</span>
              </div>
              <div className="ps-4">
                <TabButton theme={detailedMetricsTabs.map((tab) => tab.tabName)} activeTab={activeTabName} setActiveTab={setSelectedTabName} />

              </div>
              <DetailedMetricsSlowTabsFallbackWrapper loadingMessage="...loading">
                {selectedTab && (
                  <ExpandTable
                    label={`${selectedTab.tabName} Metrics`}
                    columns={selectedTab.columnNames.map((col, index) => ({
                      key: `col_${index}`,
                      label: col,
                    }))}
                    rows={selectedTab.rows.map((row) => buildMetricTableRow(
                      row,
                      lineGraphCache,
                      detailedMetricsSlowRow,
                      selectedTab.dedicatedQueryRowNames,
                      isSlowTab,
                    ))}
                    expandedRow={expandedTableRowId}
                    onExpandedRowChange={handleExpandedRowChange}
                  />
                )}
              </DetailedMetricsSlowTabsFallbackWrapper>
            </div>
            )}
          </DetailedMetricsFastTabsFallbackWrapper>
        </div>
      </div>
    </div>
  );
}
